import { useState, useMemo } from "react"
import { Layout } from "../components/Layout"
// import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/Button"
import { useToast } from "@/components/Toast"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Dialog } from "@/components/Dialog/Dialog"
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import { ColumnDef } from "@tanstack/react-table"
import { APIError } from "@/api"
import { BuildingOffice2Icon, UsersIcon, CalendarIcon, PlusIcon } from "@heroicons/react/24/outline"
import { useOrganizations, useCreateOrganization, type OrgListItem } from "@/hooks/useOrganizations"

export default function Organizations() {
  const { toast } = useToast()

  // Paging (client-side search handled by DataTable search box)
  const [page, setPage] = useState(1)
  const pageSize = 20
  const { data: orgList, isLoading, error } = useOrganizations({ page, pageSize })

  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [createForm, setCreateForm] = useState({ name: "" })
  const createOrg = useCreateOrganization()

  const items = orgList?.items ?? []
  const totalPages = orgList?.pagination.totalPages ?? 1

  const handleOrgClick = (orgPublicId: string) => {
    window.location.href = `/organizations/${orgPublicId}`
  }

  const columns = useMemo<ColumnDef<OrgListItem>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Organization",
        cell: ({ row }) => (
          <div className="flex items-center">
            <BuildingOffice2Icon className="h-5 w-5 text-secondary mr-2" />
            <span className="font-medium text-primary underline-offset-2 hover:underline cursor-pointer">
              {row.original.name}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "teams_count",
        header: "Teams",
        cell: ({ row }) => (
          <div className="flex items-center">
            <UsersIcon className="h-4 w-4 text-secondary mr-1" />
            <span className="text-sm">{row.original.teams_count ?? 0}</span>
          </div>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <div className="flex items-center">
            <CalendarIcon className="h-4 w-4 text-secondary mr-1" />
            <span className="text-sm text-secondary">
              {row.original.created_at
                ? new Date(row.original.created_at).toLocaleDateString()
                : "—"}
            </span>
          </div>
        ),
      },
    ],
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  async function onCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    const name = createForm.name.trim()
    if (!name) {
      toast({
        title: "Name required",
        description: "Please enter an organization name.",
        variant: "destructive",
      })
      return
    }
    try {
      await createOrg.mutateAsync({ name })
      toast({ title: "Organization created", description: `Created ${name}` })
      setCreateForm({ name: "" })
      setShowCreateDialog(false)
      setPage(1)
    } catch (e) {
      toast({
        title: "Error creating organization",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header like Teams page */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Organizations</h1>
            <p className="text-secondary">
              Customer &amp; billing organizations — distinct from Comms · Organizations
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button onClick={() => setShowCreateDialog(true)}>
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Organization
            </Button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : items.length === 0 ? (
          <div className="text-sm text-secondary">No organizations found.</div>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            searchKey="name"
            onRowClick={(org) => handleOrgClick(org.public_id)}
          />
        )}

        {/* Simple pager */}
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <span className="text-sm text-secondary">
            Page {page} / {totalPages}
          </span>
          <Button
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>Provide a name for the new organization.</DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreateSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={createForm.name}
                onChange={(e) => setCreateForm({ name: e.target.value })}
                placeholder="Acme Youth Football"
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createOrg.isPending || !createForm.name.trim()}>
                {createOrg.isPending ? "Creating..." : "Create Organization"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
